'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Mail, Lock, Loader2, CheckCircle2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { GoogleSignInButton } from '@/components/GoogleSignInButton'

type Mode = 'signin' | 'signup'

export function AuthModal({
  open,
  onClose,
  onAuthenticated,
  title,
  subtitle = 'Log in or create an account to join this tournament.',
  defaultMode = 'signin',
  dismissible = true,
}: {
  open: boolean
  onClose: () => void
  /** Called once a session is established while the modal is open. */
  onAuthenticated?: () => void
  title?: string
  subtitle?: string
  /** Which tab to show when the modal opens. */
  defaultMode?: Mode
  /**
   * When false, the modal can only be closed by authenticating — the close
   * button, backdrop click, and Escape key are all disabled. Use to gate a
   * page that requires a signed-in user.
   */
  dismissible?: boolean
}) {
  const { session, signInWithEmail, signUpWithEmail } = useAuth()
  const pathname = usePathname()
  const locale = pathname?.split('/')[1] || 'en'

  const [mode, setMode] = useState<Mode>(defaultMode)

  // Heading tracks the active tab (overridable via the `title` prop).
  const heading = title ?? (mode === 'signup' ? 'Create your account' : 'Sign in to continue')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmSent, setConfirmSent] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Track the session at open time so we only fire onAuthenticated on a *new* login.
  const sessionAtOpen = useRef<boolean>(false)

  // Portal target is only available on the client.
  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (open) {
      sessionAtOpen.current = !!session
      setMode(defaultMode)
      setError(null)
      setConfirmSent(false)
      setBusy(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // When a session appears while the modal is open, proceed.
  useEffect(() => {
    if (open && session && !sessionAtOpen.current) {
      onAuthenticated?.()
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, open])

  // Escape to close (disabled when the modal is non-dismissible).
  useEffect(() => {
    if (!open || !dismissible) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, dismissible])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'signin') {
        const { error } = await signInWithEmail(email.trim(), password)
        if (error) setError(error)
        // success → onAuthStateChange flips session → effect above proceeds
      } else {
        const { error, needsConfirmation } = await signUpWithEmail(email.trim(), password)
        if (error) {
          setError(error)
        } else if (needsConfirmation) {
          setConfirmSent(true)
        }
        // if no confirmation needed, session appears → effect proceeds
      }
    } finally {
      setBusy(false)
    }
  }

  if (!mounted) return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismissible ? onClose : undefined}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl"
          >
            {dismissible && (
              <button
                onClick={onClose}
                className="absolute right-4 top-4 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Close"
              >
                <X className="size-5" />
              </button>
            )}

            {confirmSent ? (
              <div className="py-4 text-center">
                <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <CheckCircle2 className="size-7" />
                </div>
                <h2 className="text-xl font-bold">Check your email</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  We sent a confirmation link to{' '}
                  <span className="font-medium text-foreground">{email}</span>. Confirm
                  it, then come back to join the tournament.
                </p>
                <Button className="mt-6 w-full" onClick={onClose}>
                  Got it
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold">{heading}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>

                {/* Google — succeeds → session appears → effect proceeds */}
                <div className="mt-5">
                  <GoogleSignInButton mode={mode} onError={setError} />
                </div>

                <div className="mb-4 flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {/* Tabs */}
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-secondary p-1">
                  {(['signin', 'signup'] as Mode[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => {
                        setMode(m)
                        setError(null)
                      }}
                      className={`rounded-md py-1.5 text-sm font-medium transition-colors ${
                        mode === m
                          ? 'bg-background text-foreground shadow'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {m === 'signin' ? 'Sign in' : 'Sign up'}
                    </button>
                  ))}
                </div>

                <form onSubmit={submit} className="mt-5 space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm outline-none focus:border-primary"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === 'signup' ? 'Create a password' : 'Your password'}
                      className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm outline-none focus:border-primary"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-400">{error}</p>
                  )}

                  <Button type="submit" className="w-full" disabled={busy}>
                    {busy && <Loader2 className="size-4 animate-spin" />}
                    {mode === 'signin' ? 'Sign in' : 'Create account'}
                  </Button>
                </form>

                <p className="mt-4 text-center text-xs text-muted-foreground">
                  More options (Google, magic link) on the{' '}
                  <Link
                    href={`/${locale}/login`}
                    className="text-primary hover:underline"
                    onClick={onClose}
                  >
                    full sign-in page
                  </Link>
                  .
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
